/**
 * Google Tag Manager Configuration
 * Environment-specific GTM settings and configuration
 * GTM and data layer only run on production domain: digipalz.io, www.digipalz.io
 */

// Single GTM Container ID for all environments
// Always use VITE_GTM_ID environment variable - must be set in all environments
const GTM_ID = import.meta.env.VITE_GTM_ID

if (!GTM_ID) {
  // VITE_GTM_ID not set; using fallback
}

// Use environment variable if available, otherwise fallback (should not happen in production)
export const GTM_CONTAINER_ID = GTM_ID || 'GTM-WZ23R6L2'

export const GTM_ALLOWED_HOSTNAMES = [
  'digipalz.io',
  'www.digipalz.io',
  'digipalz.ca',
  'www.digipalz.ca',
  'digipalz-staging.web.app',
  'digipalz-staging.firebaseapp.com'
]

export const ALLOW_LOCALHOST_FOR_GTM_TESTING = false

/**
 * Returns true when the current origin is an approved production or staging host.
 * @returns {boolean}
 */
export const isGTMAllowedByDomain = () => {
  if (typeof window === 'undefined' || !window.location?.hostname) return false
  const hostname = window.location.hostname
  return GTM_ALLOWED_HOSTNAMES.includes(hostname)
}

// GTM Container IDs for different environments (all use the same ID from VITE_GTM_ID)
const GTM_CONFIG = {
  development: {
    gtmId: GTM_CONTAINER_ID,
    dataLayerName: 'dataLayer',
    debug: true,
    preview: true
  },
  staging: {
    gtmId: GTM_CONTAINER_ID,
    dataLayerName: 'dataLayer',
    debug: true,
    preview: false
  },
  production: {
    gtmId: GTM_CONTAINER_ID,
    dataLayerName: 'dataLayer',
    debug: false,
    preview: false
  }
}

/**
 * Get GTM configuration for current environment.
 * Returns null when domain is not allowed (localhost, staging, or any non-production host).
 * @returns {object|null} GTM configuration or null if domain is blocked
 */
export const getGTMConfig = () => {
  if (!isGTMAllowedByDomain()) return null

  let environment = 'development'
  if (import.meta.env.PROD || import.meta.env.MODE === 'production') {
    environment = 'production'
  } else if (import.meta.env.MODE === 'staging') {
    environment = 'staging'
  } else {
    environment = import.meta.env.MODE || 'development'
  }

  const config = GTM_CONFIG[environment] || GTM_CONFIG.production

  if (!config.gtmId) return null

  return {
    ...config,
    environment
  }
}

/**
 * Default consent configuration
 * Used for GTM consent mode initialization
 */
export const DEFAULT_CONSENT_CONFIG = {
  // Wait for consent before loading non-essential tags
  wait_for_update: 2000, // 2 seconds timeout

  // Default consent state (before user interaction)
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'denied',
  personalization_storage: 'denied',
  security_storage: 'granted'
}

/**
 * Custom dimensions and metrics mapping
 * Map internal tracking to GTM custom dimensions/metrics
 */
export const GTM_CUSTOM_MAPPING = {
  dimensions: {
    user_type: 'custom_dimension_1', // student, educator, admin
    subscription_plan: 'custom_dimension_2', // free, premium, school
    region: 'custom_dimension_3', // EU, US, CA, etc.
    consent_status: 'custom_dimension_4', // full, partial, essential_only
    user_role: 'custom_dimension_5', // student, teacher, parent, admin
    school_type: 'custom_dimension_6', // public, private, homeschool
    grade_level: 'custom_dimension_7', // k-2, 3-5, 6-8, 9-12
    device_type: 'custom_dimension_8' // mobile, tablet, desktop
  },
  metrics: {
    session_duration: 'custom_metric_1',
    game_completion_time: 'custom_metric_2',
    lesson_progress: 'custom_metric_3',
    quiz_score: 'custom_metric_4'
  }
}

/**
 * Standard event names for consistent tracking
 */
export const GTM_EVENTS = {
  // Authentication events
  LOGIN: 'login',
  LOGOUT: 'logout',
  SIGNUP: 'sign_up',

  // Page tracking
  PAGE_VIEW: 'page_view',

  // Educational content
  LESSON_START: 'lesson_start',
  LESSON_COMPLETE: 'lesson_complete',
  QUIZ_START: 'quiz_start',
  QUIZ_COMPLETE: 'quiz_complete',

  // Game interactions
  GAME_START: 'game_start',
  GAME_COMPLETE: 'game_complete',
  GAME_LEVEL_UP: 'level_up',
  ACHIEVEMENT_UNLOCK: 'achievement_unlock',

  // E-commerce
  PURCHASE: 'purchase',
  SUBSCRIPTION_START: 'subscription_start',
  SUBSCRIPTION_CANCEL: 'subscription_cancel',

  // Engagement
  VIDEO_PLAY: 'video_play',
  VIDEO_COMPLETE: 'video_complete',
  DOWNLOAD: 'download',
  SEARCH: 'search',

  // Cookie consent
  CONSENT_GRANTED: 'consent_granted',
  CONSENT_DENIED: 'consent_denied',
  CONSENT_UPDATED: 'consent_updated',

  // Custom events
  CUSTOM_EVENT: 'custom_event'
}

/**
 * Enhanced ecommerce product categories
 */
export const PRODUCT_CATEGORIES = {
  SUBSCRIPTION: 'subscription',
  INDIVIDUAL_LESSON: 'lesson',
  GAME_ACCESS: 'game',
  PREMIUM_FEATURE: 'feature',
  CERTIFICATION: 'certification'
}

/**
 * User journey stages for funnel analysis
 */
export const USER_JOURNEY_STAGES = {
  VISITOR: 'visitor',
  REGISTERED: 'registered',
  ONBOARDED: 'onboarded',
  ACTIVE_USER: 'active_user',
  PREMIUM_USER: 'premium_user',
  CHURNED: 'churned'
}

export default {
  getGTMConfig,
  isGTMAllowedByDomain,
  GTM_ALLOWED_HOSTNAMES,
  DEFAULT_CONSENT_CONFIG,
  GTM_CUSTOM_MAPPING,
  GTM_EVENTS,
  PRODUCT_CATEGORIES,
  USER_JOURNEY_STAGES
}
